import PropTypes from 'prop-types';
import React, { Suspense } from 'react';
import { connect } from 'react-redux';
import { Transition as ReactTransition } from 'react-transition-group';
import Browser from '@wetransfer/concorde-browser';

import PanelErrorBoundary from './panel-error-boundary';
import PanelTopBar from './panel-top-bar';

import Transition from './transition';

import Router from '@module/router';
import I18n from '@module/i18n';
import Analytics from '@module/analytics';
import LaunchDarkly from '@module/launchdarkly';

import { shouldAskBeforeLeaving } from '@app/helper/route.js';
import { isLoggedInPlusUser } from '@app/helper/user';

import Actions from '@app/store/action';

import Footer from '@component/footer';
import Scrollable from '@component/scrollable';
import Button from '@component/form/element/button';
import NotificationBar from '@component/info/notificationbar';
import Notification from '@component/info/notification';
import ActionBar from '@component/info/actionbar';
import AccountWarning from '@component/info/accountwarning';

import {
  FF_FORCE_ACCOUNT_VERIFICATION,
  FF_COOKIE_WALL,
} from '@app/constant/experiments';
import { STATUS } from '@constant/subscriptions';
import { AUTH_STATE } from '@app/constant/auth';
import { TRANSFER_GROUP } from '@app/constant/transfer-group';

export class Panel extends React.PureComponent {
  /**
   * Binds all private methods
   * @param  {Object} props Component props
   */
  constructor(props) {
    super(props);

    // We'll store the current `route.path` and the corresponding `content`
    // inside the component state.
    this.state = {
      returnPath: null,
      returnName: null,
    };

    this.closePanel = this.closePanel.bind(this);
    this.leavePage = this.leavePage.bind(this);
    this.setReturnPath = this.setReturnPath.bind(this);
    this.stayOnPage = this.stayOnPage.bind(this);
    this.onEnter = this.onEnter.bind(this);
    this.onExit = this.onExit.bind(this);
  }

  onExit(component) {
    if (this.currentTransition) {
      this.currentTransition.stop(true);
    }

    this.currentTransition = new Transition({
      prevType: false,
      newType: this.props.route.type,
      component: component,
      animate: false,
    });
  }

  onEnter(component) {
    if (this.currentTransition) {
      this.currentTransition.stop(true);
    }
    this.currentTransition = new Transition({
      prevType: false,
      newType: this.props.route.type,
      component: component,
      animate: !this.props.appear,
    });
  }

  // When component is updated
  componentDidUpdate(prevProps) {
    // This will stop updates from happening if the panel is leaving
    // and the current route is transfer (so -> no panel)
    const { route } = this.props;

    if (this.currentTransition) {
      this.currentTransition.stop(true);
    }

    if (this.node) {
      this.currentTransition = new Transition({
        prevType: prevProps.route.type,
        newType: route.type,
        component: this.node,
      });
    }

    if (prevProps.route.path !== route.path) {
      this.props.resetNotification();
      if (this.scrollableNode) {
        this.scrollableNode.scrollTo('top');
      }
    }
  }

  // Set the returnPath for a panel
  setReturnPath(path, name) {
    this.setState({ returnPath: path, returnName: name });
  }

  componentWillUnmount() {
    clearTimeout(this.scrollTimer);
    this.props.setFormState(false);
  }

  closePanel() {
    const lastRoute = Router.getLastRoute('transfer', '/');
    // The "/account-verification" route is a transfer route, which means it does something
    // but with the panel closed. When we close the panel, we want to navigate to "/" instead
    // of "/account-verification" otherwise we end up in a loop, because "/account-verification"
    // requires logged in users.
    const navigateTo = lastRoute === '/account-verification' ? '/' : lastRoute;
    Router.navigateTo(navigateTo);
  }

  resetReturnPath = () => {
    this.setReturnPath(null, null);
  };

  stayOnPage() {
    this.props.setNextAction(null);
  }

  leavePage() {
    const { route } = this.props;

    if (route.isMobileTransfer) {
      // create a new transfer to delete the current one
      this.props.createTransfer();
    }

    this.props.setFormState(false);
    route.nextRoute();
  }

  renderNotificationBar(notification) {
    if (notification.panel.html === undefined) {
      return null;
    }

    return <NotificationBar key="panel_notification_bar" />;
  }

  makeInactiveAccountWarning(account) {
    const expired = account.subscription.status === STATUS.CANCELED;
    const titleKey = expired
      ? 'panel.warning.account_expired_title'
      : 'panel.warning.account_inactive_title';
    const subtitleKey = expired
      ? 'panel.warning.account_expired_subtitle'
      : 'panel.warning.account_inactive_subtitle';
    const actionTextKey = expired
      ? 'panel.warning.account_expired_action'
      : 'panel.warning.account_inactive_action';
    const action = () => {
      Router.navigateTo('/payment/retry', false);
    };
    return (
      <AccountWarning
        key="panel_account_warning"
        title={I18n.t(titleKey)}
        subtitle={I18n.t(subtitleKey)}
        actionText={I18n.t(actionTextKey)}
        action={action}
      />
    );
  }

  makeUnverifiedWarning(user) {
    const action = () => {
      Router.navigateTo('/upload');
    };

    return (
      <Notification key="notification" className="notification--verification">
        <h2 className="notification__title">
          {I18n.t('panel.warning.email_unverified_title')}
        </h2>
        <p
          dangerouslySetInnerHTML={{
            __html: I18n.t('panel.warning.email_unverified_text_link', {
              email: user.email,
              href: '/account',
            }),
          }}
        />
        {Browser.isMobile && (
          <Button
            className="notification--verification-button"
            type="inline"
            onClick={action}
            text={I18n.t('panel.warning.email_unverified_mobile_cta')}
          />
        )}
      </Notification>
    );
  }

  makeAccountVerificationLoginWarning() {
    return (
      <Notification
        key="notification"
        className="notification--verification-login"
      >
        <p>{I18n.t('panel.warning.account_email_verify_logged_out')}</p>
      </Notification>
    );
  }

  makeForceAccountVerificationWarning() {
    return (
      <Notification key="notification" className="notification--verification">
        <h2 className="notification__title">
          {I18n.t('panel.warning.email_unverified_title')}
        </h2>
        <p>{I18n.t('panel.warning.force-account-verification.body')}</p>
      </Notification>
    );
  }

  makeReceivingEmailWarning({ accountEmail, receivingEmail }) {
    return (
      <Notification
        key="notification"
        className="notification--receiving-email"
        showClose={true}
      >
        <p
          className="notification--receiving-email-text"
          dangerouslySetInnerHTML={{
            __html: I18n.t('panel.warning.receiving-email', {
              account_email: accountEmail,
              receiving_email: receivingEmail,
            }),
          }}
        />
      </Notification>
    );
  }

  shouldSeeTandCBanner() {
    const { route, session } = this.props;

    return (
      !session.tandc &&
      !route.isMobileTransfer &&
      !LaunchDarkly.getVariant(FF_COOKIE_WALL)
    );
  }

  renderActionBar() {
    const { route, transfer } = this.props;

    if (route.nextRoute && shouldAskBeforeLeaving(route, transfer)) {
      return (
        <ActionBar
          ref={(node) => (this.actionBarNode = node)}
          text={I18n.t('form.confirm_leave')}
          key="panel_action"
        >
          <Button
            type="actionbar-action"
            text={I18n.t('form.stay_on_page')}
            onClick={this.stayOnPage}
          />
          <Button
            type="actionbar-cancel"
            text={I18n.t('form.leave')}
            onClick={this.leavePage}
          />
        </ActionBar>
      );
    }

    if (this.shouldSeeTandCBanner()) {
      return (
        <ActionBar
          ref={(node) => (this.actionBarNode = node)}
          html={I18n.t('panel.cookie.text')}
          key="panel_action"
        >
          <Button
            type="actionbar-action"
            text={I18n.t('panel.cookie.accept')}
            onClick={this.grantConsent}
          />
        </ActionBar>
      );
    }
  }

  componentDidMount() {
    if (this.shouldSeeTandCBanner()) {
      Analytics.trackSnowplowEvent({
        category: 'transfer',
        action: 'show_cookie_banner',
        label: 'okay',
      });
    }
  }

  grantConsent = () => {
    Analytics.trackSnowplowEvent({
      category: 'transfer',
      action: 'click_give_consent',
      label: 'okay',
    });
    this.props.acceptTermsAndConditions();
  };

  render() {
    const { account, route, user, notification } = this.props;
    const contents = [];
    const shouldShowInActiveWarning =
      !account.active_subscription &&
      route.requiredAuthState === AUTH_STATE.LOGGED_IN &&
      !route.payment &&
      isLoggedInPlusUser(user);
    const shouldShowUnverifiedWarning =
      !user.isVerified &&
      route.requiredAuthState === AUTH_STATE.LOGGED_IN &&
      !route.payment &&
      !route.path.includes('account') &&
      !route.path.includes('payment') &&
      user.loggedIn &&
      !user.forceVerificationEnabled;
    const shouldShowAccountVerificationLoginWarning =
      route.query.sig && route.path.includes('log-in') && !user.loggedIn;
    const shouldShowReceivingEmailWarning =
      route.path.includes('transfers') &&
      route.query.group === TRANSFER_GROUP.RECEIVED &&
      route.query.receiving_email &&
      user.isVerified &&
      user.loggedIn &&
      !user.hasSkippedVerification &&
      account.receiving_email_confirmed &&
      LaunchDarkly.getVariant(FF_FORCE_ACCOUNT_VERIFICATION);
    const shouldShowForceAccountVerificationWarning =
      !user.isVerified &&
      route.requiredAuthState === AUTH_STATE.LOGGED_IN &&
      !route.payment &&
      !route.path.includes('account') &&
      !route.path.includes('payment') &&
      user.loggedIn &&
      user.forceVerificationEnabled;

    //Users need to pay first, then verify
    if (shouldShowInActiveWarning) {
      contents.push(this.makeInactiveAccountWarning(account));
    }
    if (shouldShowUnverifiedWarning && !shouldShowInActiveWarning) {
      contents.push(this.makeUnverifiedWarning(user));
    }
    if (shouldShowAccountVerificationLoginWarning) {
      contents.push(this.makeAccountVerificationLoginWarning());
    }
    if (shouldShowReceivingEmailWarning && !shouldShowInActiveWarning) {
      contents.push(
        this.makeReceivingEmailWarning({
          accountEmail: user.email,
          receivingEmail: route.query.receiving_email,
        })
      );
    }
    if (shouldShowForceAccountVerificationWarning) {
      contents.push(this.makeForceAccountVerificationWarning());
    }

    const props = {
      locale: this.props.locale,
      route: route,
      page: this,
      key: 'panel_route_component',
    };
    props.setReturnPath = this.setReturnPath;

    /*
     * These are components declared in navigation/config.js
     * By default, each component created will have
     * locale, route, page, setReturnPath as props
     */
    const Component = route.component;

    Component &&
      contents.push(
        <Suspense key="panel_content" fallback={null}>
          <PanelErrorBoundary>
            <Component {...props} />
          </PanelErrorBoundary>
        </Suspense>
      );

    // content
    const panelContentClassName = ['panel__content'];
    if (
      shouldShowInActiveWarning ||
      shouldShowUnverifiedWarning ||
      shouldShowForceAccountVerificationWarning
    ) {
      panelContentClassName.push('panel__content--inactive-content');
    }

    // Add route name to the panel for custom behavior/styling
    if (this.props.route.name) {
      panelContentClassName.push(this.props.route.name);
    }

    let content = [
      <div key="panel_content" className={panelContentClassName.join(' ')}>
        {contents}
      </div>,
      this.renderNotificationBar(notification),
      this.renderActionBar(),
    ];

    if (!Browser.isMobile) {
      content.push(<Footer key="panel_footer" />);
    }

    if (!Browser.isMobile) {
      content = (
        <Scrollable
          className="panel__scrollable"
          ref={(node) => (this.scrollableNode = node)}
        >
          {content}
        </Scrollable>
      );
    }

    return (
      <ReactTransition
        appear={this.props.appear}
        in={this.props.in}
        timeout={{ in: 0, exit: 1000 }}
        onEnter={this.onEnter}
        onEntered={this.onEntered}
        onExit={this.onExit}
        unmountOnExit={true}
      >
        <div
          className="panel panel--no-transition"
          ref={(node) => (this.node = node)}
        >
          {!Browser.isMobile && (
            <PanelTopBar
              returnName={this.state.returnName}
              returnPath={this.state.returnPath}
              onCloseClick={this.closePanel}
              onBackClick={this.resetReturnPath}
            />
          )}
          {content}
        </div>
      </ReactTransition>
    );
  }
}

Panel.propTypes = {
  appear: PropTypes.bool,
  in: PropTypes.bool,
  locale: PropTypes.string,
  route: PropTypes.object,
  transfer: PropTypes.object,
  account: PropTypes.object,
  user: PropTypes.object,
  notification: PropTypes.object,
  session: PropTypes.object,
  resetNotification: PropTypes.func.isRequired,
  setFormState: PropTypes.func.isRequired,
  createTransfer: PropTypes.func.isRequired,
  setNextAction: PropTypes.func.isRequired,
  acceptTermsAndConditions: PropTypes.func.isRequired,
};

const mapStateToProps = ({
  account,
  route,
  transfer,
  layout,
  user,
  notification,
  session,
}) => ({
  account,
  route,
  transfer,
  layout,
  user,
  notification,
  session,
});

export default connect(mapStateToProps, {
  resetNotification: Actions.Notification.resetNotification,
  setFormState: Actions.Route.setFormState,
  createTransfer: Actions.Transfer.create,
  setNextAction: Actions.Route.setNextAction,
  acceptTermsAndConditions: Actions.Session.acceptTermsConditions,
})(Panel);
